import type { AccessTokenPayload } from '@/api/auth/interfaces';
import { canAccessAdminArea, normalizeUserRole } from '@/utils/authz';
import { USER_ROLES } from '@/constants/roles';

/**
 * Determines the appropriate redirect route based on user role and authentication status.
 */
export function getRedirectRoute(payload: AccessTokenPayload | null): string {
  if (!payload) {
    return '/login';
  }

  const role = normalizeUserRole(payload.role);
  if (!role) {
    return '/login';
  }

  if (canAccessAdminArea(role)) {
    return '/admin';
  }

  if (role === USER_ROLES.USER) {
    return '/manage';
  }

  if (role === USER_ROLES.CUSTOMER) {
    return '/client';
  }

  return '/';
}

/**
 * Validates a post-login redirect target is same-origin.
 */
export function getSafeRedirectPath(redirect?: string): string | undefined {
  if (!redirect?.trim()) {
    return undefined;
  }

  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    if (redirect.startsWith('/') && !redirect.startsWith('//')) {
      return redirect;
    }
    return undefined;
  }
}
